import React from 'react';
import { Steps, Tag, Typography, Space, Tooltip, Badge } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    LoadingOutlined,
    CloseCircleOutlined,
    MinusCircleOutlined,
    SendOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import type { OperationMissionItem } from './operationModels';
import { opMissionStatusColor } from './operationModels';

const { Text } = Typography;

interface OperationTimelineProps {
    operationMissions: OperationMissionItem[];
}

const statusIcon = (status: string) => {
    switch (status) {
        case 'completed':
            return <CheckCircleOutlined style={{ color: '#722ed1' }} />;
        case 'running':
            return <LoadingOutlined style={{ color: '#52c41a' }} />;
        case 'dispatched':
            return <SendOutlined style={{ color: '#13c2c2' }} />;
        case 'ready':
            return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
        case 'failed':
            return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        case 'skipped':
            return <MinusCircleOutlined style={{ color: '#8c8c8c' }} />;
        default:
            return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
};

const stepStatus = (status: string): 'wait' | 'process' | 'finish' | 'error' => {
    switch (status) {
        case 'completed':
            return 'finish';
        case 'running':
        case 'dispatched':
            return 'process';
        case 'failed':
            return 'error';
        default:
            return 'wait';
    }
};

const OperationTimeline: React.FC<OperationTimelineProps> = ({ operationMissions }) => {
    const sorted = [...operationMissions].sort((a, b) => a.executionOrder - b.executionOrder);

    if (sorted.length === 0) {
        return <Text type="secondary">No missions in this operation</Text>;
    }

    return (
        <Steps
            direction="horizontal"
            size="small"
            className="operation-timeline-steps"
            items={sorted.map((om) => {
                const missionName = om.mission?.name || `Mission #${om.missionId}`;
                const deviceName = om.device?.label || om.mission?.device?.label || '—';
                const deps = om.dependsOnOperationMissionIds || [];

                return {
                    title: (
                        <Tooltip title={`${missionName} — ${om.status}`}>
                            <Text strong style={{ fontSize: 12 }}>{missionName}</Text>
                        </Tooltip>
                    ),
                    description: (
                        <Space direction="vertical" size={0} style={{ lineHeight: 1.3 }}>
                            <Space size={4}>
                                <RobotOutlined style={{ fontSize: 11 }} />
                                <Text type="secondary" style={{ fontSize: 11 }}>{deviceName}</Text>
                            </Space>
                            <Tag
                                color={opMissionStatusColor(om.status)}
                                style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
                            >
                                {om.status}
                            </Tag>
                            {((om as any).triggerCondition || om.triggerType) !== 'auto' && (
                                <Badge
                                    status={((om as any).triggerCondition || om.triggerType) === 'manual' ? 'warning' : 'processing'}
                                    text={<Text style={{ fontSize: 10 }}>{(om as any).triggerCondition || om.triggerType}</Text>}
                                />
                            )}
                            {deps.length > 0 && (
                                <Text style={{ fontSize: 10, color: '#8c8c8c' }}>
                                    depends on: {deps.map((d) => `#${d}`).join(', ')}
                                </Text>
                            )}
                        </Space>
                    ),
                    status: stepStatus(om.status),
                    icon: statusIcon(om.status),
                };
            })}
        />
    );
};

export default OperationTimeline;
